/**
 * useStep8Concept — Step 8 lifecycle.
 *
 * The `cybersecurity_concept_agent` emits TWO sibling arrays in one
 * result envelope:
 *   - `cybersecurity_controls`     (per ISO 21434 RQ-09-08)
 *   - `cybersecurity_requirements` (per RQ-09-09)
 *
 * Mirror of Step 7's dual-collection hook. The same unified-approve
 * pattern applies: per-collection modifications state, single
 * `approveHierarchicalData` call covering the merged payload.
 *
 * Forms:
 *   - Controls modal → goal_id select from Step 7 goals fetch
 *   - Requirements modal → control_id select from THIS step's
 *     `controls` array (self-reference)
 */
import { useCallback, useState } from 'react';
import { getAgentResult, approveHierarchicalData } from '../../../api/workflow';
import { notify } from '../../../services/notificationBus';
import { useStructuredFetch } from '../hooks/useStructuredFetch';
import { useRefetchOnReviseComplete } from '../hooks/useRefetchOnReviseComplete';
import { useRecordCRUD } from '../hooks/useRecordCRUDv2';
import type { Step8BodyProps } from '../step-components/stepBodyProps';
import type {
  HierarchicalModifications,
  PerStepState,
  WorkflowActions,
  WorkflowV2State,
} from '../state/workflowV2Types';
import type { OptionLite } from '../step-components/step8-concept/ControlFormModal';
import type { ControlRecord } from '../step-components/step8-concept/controlsFieldSchema';
import type { RequirementRecord } from '../step-components/step8-concept/requirementsFieldSchema';

const HAS_OUTPUT = new Set(['draft', 'revising', 'approved']);
const AGENT_NAME = 'cybersecurity_concept_agent';
const GOALS_AGENT_NAME = 'cybersecurity_goals_agent';

interface AgentResultResponse {
  result?: Record<string, unknown>;
  [key: string]: unknown;
}

interface GoalLite {
  goal_id?: string;
  goal_statement?: string;
  [key: string]: unknown;
}

interface ControlsReqsPayload {
  controls: ControlRecord[];
  requirements: RequirementRecord[];
}

export interface UseStep8Input {
  threadId: string | null | undefined;
  stepState: PerStepState | undefined;
  workflowCompleted: boolean;
  analysisMode?: string | null;
  /** Reducer state — Story H8 lifts the dual-collection modifications
   *  (controls + requirements) into `state.steps[8].hierarchicalModifications`
   *  so they survive step navigation. Mirrors Steps 2-6 (H2) + Step 7 (H8). */
  state: WorkflowV2State;
  actions: WorkflowActions;
}

export function useStep8Concept({
  threadId,
  stepState,
  workflowCompleted,
  analysisMode,
  state,
  actions,
}: UseStep8Input): Step8BodyProps {
  const status = stepState?.status ?? 'empty';
  const hasOutput = HAS_OUTPUT.has(status);
  const editable = workflowCompleted || status === 'draft';

  /* ----- Controls + Requirements fetch (one API call, two arrays) ----- */

  const fetchConcept = useCallback(
    (_signal: AbortSignal): Promise<AgentResultResponse | null> => {
      if (!threadId) return Promise.resolve(null);
      return getAgentResult(threadId, AGENT_NAME) as unknown as Promise<AgentResultResponse>;
    },
    [threadId],
  );

  const extractConcept = useCallback(
    (raw: AgentResultResponse | null): ControlsReqsPayload => {
      const r = raw?.result as Record<string, unknown> | undefined;
      const controls = (r?.cybersecurity_controls ?? r?.controls) as ControlRecord[] | undefined;
      const requirements = (r?.cybersecurity_requirements ?? r?.requirements) as
        | RequirementRecord[]
        | undefined;
      return {
        controls: Array.isArray(controls) ? controls : [],
        requirements: Array.isArray(requirements) ? requirements : [],
      };
    },
    [],
  );

  const structured = useStructuredFetch<AgentResultResponse | null, ControlsReqsPayload>({
    fetcher: fetchConcept,
    extract: extractConcept,
    enabled: hasOutput && !!threadId,
  });
  useRefetchOnReviseComplete(status, structured.refetch);

  const controls = structured.data?.controls ?? [];
  const requirements = structured.data?.requirements ?? [];

  /* ----- Goals list fetch (controls modal's goal_id select) ----- */

  const fetchGoals = useCallback(
    (_signal: AbortSignal): Promise<AgentResultResponse | null> => {
      if (!threadId) return Promise.resolve(null);
      return getAgentResult(threadId, GOALS_AGENT_NAME) as unknown as Promise<AgentResultResponse>;
    },
    [threadId],
  );

  const extractGoals = useCallback((raw: AgentResultResponse | null): GoalLite[] => {
    const r = raw?.result as Record<string, unknown> | undefined;
    const list = (r?.cybersecurity_goals ?? r?.goals) as GoalLite[] | undefined;
    return Array.isArray(list) ? list : [];
  }, []);

  const goalsFetch = useStructuredFetch<AgentResultResponse | null, GoalLite[]>({
    fetcher: fetchGoals,
    extract: extractGoals,
    enabled: editable && !!threadId,
  });

  // Dedupe by goal_id (child-TARA collisions). First label wins.
  const goalOptions: OptionLite[] = (() => {
    const seen = new Set<string>();
    const out: OptionLite[] = [];
    for (const g of goalsFetch.data ?? []) {
      const id = String(g.goal_id ?? '');
      if (!id || seen.has(id)) continue;
      seen.add(id);
      const desc = g.goal_statement
        ? String(g.goal_statement).slice(0, 50) + (String(g.goal_statement).length > 50 ? '…' : '')
        : '';
      out.push({ value: id, label: desc ? `${id} — ${desc}` : id });
    }
    return out;
  })();

  // Self-reference: requirements modal's control_id select pulls from
  // the very controls list rendered above on the page. Same dedupe
  // pattern as the goals list to handle child-TARA collisions.
  const controlOptions: OptionLite[] = (() => {
    const seen = new Set<string>();
    const out: OptionLite[] = [];
    for (const c of controls) {
      const id = String(c.control_id ?? '');
      if (!id || seen.has(id)) continue;
      seen.add(id);
      const desc = c.control_name
        ? String(c.control_name).slice(0, 50) + (String(c.control_name).length > 50 ? '…' : '')
        : '';
      out.push({ value: id, label: desc ? `${id} — ${desc}` : id });
    }
    return out;
  })();

  /* ----- CRUD ----- */

  const crud = useRecordCRUD({
    threadId,
    agentName: AGENT_NAME,
    enabled: editable,
  });

  /* ----- Unified hierarchical approve (one API call covers both lists) ----- */

  const hierarchicalEnabled = analysisMode === 'hierarchical';

  // Story H8 — single reducer-backed map covers BOTH controls and
  // requirements modifications. `control_id` and `req_id` namespaces
  // don't collide, so a flat `Record<string, ...>` is safe.
  const modifications = state.steps[8]?.hierarchicalModifications ?? {};
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState<string | null>(null);

  const setControlRelevance = useCallback(
    (id: string, isRelevant: boolean) => actions.setHierRelevance(8, id, isRelevant),
    [actions],
  );
  const setControlJustification = useCallback(
    (id: string, justification: string) => actions.setHierJustification(8, id, justification),
    [actions],
  );
  const setReqRelevance = useCallback(
    (id: string, isRelevant: boolean) => actions.setHierRelevance(8, id, isRelevant),
    [actions],
  );
  const setReqJustification = useCallback(
    (id: string, justification: string) => actions.setHierJustification(8, id, justification),
    [actions],
  );

  const filterApproved = useCallback(
    (
      records: Array<Record<string, unknown>>,
      primaryKey: string,
      mods: HierarchicalModifications,
    ): Array<Record<string, unknown>> => {
      const newItems = records.filter((r) => r?.source !== 'child_tara');
      const children = records.filter((r) => r?.source === 'child_tara');
      const relevant = children.filter((c) => {
        const id = String(c[primaryKey] ?? '');
        const override = mods[id]?.is_relevant;
        if (override === undefined) return c.is_relevant !== false;
        return override !== false;
      });
      return [...newItems, ...relevant];
    },
    [],
  );

  const unifiedApprove = useCallback(async () => {
    if (!hierarchicalEnabled) return;
    if (!threadId) throw new Error('No active workflow session');
    setApproving(true);
    setApproveError(null);
    try {
      const approvedControls = filterApproved(
        controls as Array<Record<string, unknown>>,
        'control_id',
        modifications,
      );
      const approvedReqs = filterApproved(
        requirements as Array<Record<string, unknown>>,
        'req_id',
        modifications,
      );
      // Single API call covers both collections; backend's
      // `approve-hierarchical` handler distinguishes by record shape.
      // The merged `modifications` map carries both subsets — backend
      // ignores entries that don't match any record in the agent's
      // result, so the merge is safe.
      await approveHierarchicalData(
        threadId,
        AGENT_NAME,
        [...approvedControls, ...approvedReqs],
        modifications,
      );
      // Story H8 — clear the reducer-backed diff map so the next
      // edit session starts fresh (mirrors `useHierarchicalApprove`).
      actions.clearHierModifications(8);
      structured.refetch();
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || 'Failed to approve relevance';
      const message = typeof msg === 'string' ? msg : 'Failed to approve relevance';
      setApproveError(message);
      // Story 15.G — bring toast parity with `useHierarchicalApprove`
      // (Step 8's dual-collection unifiedApprove can't use the shared
      // hook so the notify lives here too).
      notify({ type: 'error', title: 'Save failed', message });
      throw e;
    } finally {
      setApproving(false);
    }
  }, [hierarchicalEnabled, threadId, controls, requirements, modifications, filterApproved, structured, actions]);

  return {
    status,
    errorMessage: stepState?.errorMessage ?? null,
    editable,
    structured,
    controls,
    requirements,
    crud,
    goalOptions,
    controlOptions,
    threadId,
    analysisMode,
    controlsHierarchical: {
      enabled: hierarchicalEnabled,
      modifications,
      setRelevance: setControlRelevance,
      setJustification: setControlJustification,
      onApprove: unifiedApprove,
      approving,
      approveError,
    },
    requirementsHierarchical: {
      enabled: hierarchicalEnabled,
      modifications,
      setRelevance: setReqRelevance,
      setJustification: setReqJustification,
      onApprove: unifiedApprove,
      approving,
      approveError,
    },
    approveOverride: hierarchicalEnabled
      ? { onApprove: unifiedApprove, approving }
      : undefined,
  };
}
